// ─────────────────────────────────────────
//  CONVERSATION MEMORY
// ─────────────────────────────────────────

import { S, sb } from './state.js';
import { showToast } from './utils.js';
import { addMsg } from './ui.js';
import { extractCode } from './utils.js';
import { applyCode } from './editor.js';
import { runCode } from './preview.js';
import { hideGallery } from './templates.js';

export function loadActiveConversation() {
  if (!S.session) return;

  sb.from('conversations')
    .select('id, messages, code, mode, title, memory')
    .eq('user_id', S.session.user.id)
    .eq('is_active', true)
    .single()
    .then(({ data: conv }) => {
      if (conv && conv.messages && conv.messages.length > 0) {
        S.convId = conv.id;
        S.history = conv.messages;
        S.code = conv.code || '';
        S.mode = conv.mode || 'html';
        S.memory = conv.memory || '';

        // Restore code editor
        if (S.code) {
          applyCode(S.code);
          const modeStat = document.getElementById('mode-stat');
          if (modeStat) modeStat.textContent = S.mode.toUpperCase();
        }

        // Restore chat messages
        hideGallery();
        const msgs = document.getElementById('messages');
        if (!msgs) return;
        const welcome = document.getElementById('welcome-msg');
        msgs.innerHTML = '';
        if (welcome) msgs.appendChild(welcome);

        for (const m of S.history) {
          addMsg(m.role, m.content, m.role === 'assistant' && extractCode(m.content));
        }

        // Run the code in preview
        if (S.code) runCode();

        showToast('✓ Resumed your session');
      }
    })
    .catch(() => {
      // No active conversation — fresh start
    });
}

export function saveConversation() {
  if (!S.session || S.history.length === 0) return;

  // Generate title from first user message
  const firstMsg = S.history.find(m => m.role === 'user');
  const title = firstMsg ? firstMsg.content.slice(0, 60) : 'Untitled';

  // Generate a memory summary for long-term context
  const memory = buildMemorySummary();

  if (S.convId) {
    // Update existing conversation
    sb.from('conversations')
      .update({
        messages: S.history.slice(-30), // Keep last 30 messages (trim old ones)
        code: S.code,
        mode: S.mode,
        title,
        memory,
        project_id: S.projectId || null,
      })
      .eq('id', S.convId)
      .then(() => {})
      .catch(() => {});
  } else {
    // Deactivate any existing active conversation, then create new
    sb.from('conversations')
      .update({ is_active: false })
      .eq('user_id', S.session.user.id)
      .eq('is_active', true)
      .then(() => {
        sb.from('conversations')
          .insert({
            user_id: S.session.user.id,
            messages: S.history.slice(-30),
            code: S.code,
            mode: S.mode,
            title,
            memory,
            is_active: true,
            project_id: S.projectId || null,
          })
          .select('id')
          .single()
          .then(({ data: newConv }) => {
            if (newConv) S.convId = newConv.id;
          })
          .catch(() => {});
      })
      .catch(() => {});
  }
}

// ── Build a condensed memory summary for cross-session AI context ──
function buildMemorySummary() {
  const userMsgs = S.history.filter(m => m.role === 'user').map(m => m.content);
  if (!userMsgs.length) return '';

  // Extract key info from the conversation
  const parts = [];

  // What was originally requested
  parts.push('ORIGINAL REQUEST: ' + userMsgs[0].slice(0, 200));

  // Subsequent modifications (summarize)
  if (userMsgs.length > 1) {
    const mods = userMsgs.slice(1).map(m => m.slice(0, 80));
    parts.push('MODIFICATIONS: ' + mods.join(' | '));
  }

  // Current app state
  if (S.code) {
    const lines = S.code.split('\n').length;
    // Extract title from code if present
    const titleMatch = S.code.match(/<title>([^<]+)<\/title>/i);
    const appTitle = titleMatch ? titleMatch[1] : '';
    parts.push(`CURRENT STATE: ${lines} lines, mode=${S.mode}${appTitle ? ', title="' + appTitle + '"' : ''}`);
  }

  return parts.join('\n');
}

export function clearConversation() {
  if (S.convId && S.session) {
    sb.from('conversations')
      .update({ is_active: false })
      .eq('id', S.convId)
      .then(() => {})
      .catch(() => {});
    S.convId = null;
  }
  S.memory = '';
}
